import { MoonIcon } from '@icons/moon-icon';
import { SunIcon } from '@icons/sun-icon';
import { Switch, SwitchProps, useSwitch } from '@nextui-org/react';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import clsx from 'clsx';
import { useTheme } from 'next-themes';
import { FC, useEffect, useState } from 'react';

export interface ThemeSwitchProps {
	className?: string;
	classNames?: SwitchProps['classNames'];
	onChange?: (isDarkMode: boolean) => void;
}

export const ThemeSwitch: FC<ThemeSwitchProps> = ({ className, classNames, onChange }) => {
	const [isMounted, setIsMounted] = useState(false);
	const { theme, setTheme } = useTheme();

	const handleChange = () => {
		const isDarkMode = theme === 'light';
		setTheme(isDarkMode ? 'dark' : 'light');
		if (onChange) {
			onChange(isDarkMode);
		}
	};

	const { Component, slots, isSelected, getBaseProps, getInputProps, getWrapperProps } = useSwitch({
		isSelected: theme === 'light',
		onChange: handleChange,
	});

	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Prevent Hydration Mismatch
	if (!isMounted) {
		return <div className='w-6 h-6' />;
	}

	return (
		<Component
			{...getBaseProps({
				className: clsx('px-px transition-opacity hover:opacity-80 cursor-pointer', className, classNames?.base),
			})}
		>
			<VisuallyHidden>
				<input {...getInputProps()} />
			</VisuallyHidden>
			<div
				{...getWrapperProps()}
				className={slots.wrapper({
					class: clsx(
						[
							'w-auto h-auto',
							'bg-transparent',
							'rounded-lg',
							'flex items-center justify-center',
							'group-data-[selected=true]:bg-transparent',
							'!text-default-500',
							'pt-px',
							'px-0',
							'mx-0',
						],
						classNames?.wrapper,
					),
				})}
			>
				<Switch
					defaultSelected
					checked={isSelected}
					onChange={handleChange}
					size='lg'
					color='primary'
					startContent={<SunIcon />}
					endContent={<MoonIcon />}
				/>
			</div>
		</Component>
	);
};
